import securityService from '@/services/security';
import emailDomainValidator from './emailDomainValidator';

export interface ValidationResult {
  isValid: boolean;
  error: string;
}

class FormValidator {
  constructor() {
    // Asinhrono inicijalizuj blacklist pri kreiranju
    this.initializeAsync();
  }

  /**
   * Asinhrona inicijalizacija
   */
  private async initializeAsync(): Promise<void> {
    try {
      await emailDomainValidator.initializeBlacklist();
      console.log(
        `Frontend - EmailDomainValidator inicijalizovan sa ${emailDomainValidator.getBlacklistSize()} blacklist domena`
      );

      // Test u development mode
      if (process.env.NODE_ENV === 'development') {
        this.testEmailDomainValidation();
      }
    } catch (error) {
      console.warn(
        'Frontend - Greška pri inicijalizaciji EmailDomainValidator:',
        error
      );
    }
  }

  /**
   * Test funkcija za email domain validaciju
   */
  private testEmailDomainValidation(): void {
    const testEmails = [
      'test@gmail.com', // Treba da prođe (allowed list)
      'test@10minutemail.com', // Treba da se blokira (GitHub blacklist)
      'test@fastmail.com', // Treba da prođe (mailchecker OK)
      'test@yopmail.com', // Treba da se blokira (disposable)
    ];

    console.log('=== FRONTEND FORM VALIDATOR TESTIRANJE ===');
    testEmails.forEach((email) => {
      const result = this.validateEmailDomain(email);
      console.log(
        `${email}: ${result.isValid ? 'DOZVOLJEN' : 'BLOKIRAN'} - ${
          result.error || 'OK'
        }`
      );
    });
    console.log('=== KRAJ FORM VALIDATOR TESTIRANJA ===');
  }

  /**
   * Validates email domain using enhanced emailDomainValidator
   * @param {string} email - The email to validate
   * @returns {ValidationResult} - Result with isValid and error message
   */
  validateEmailDomain(email: string): ValidationResult {
    const result = emailDomainValidator.validateEmailDomain(email);
    return {
      isValid: result.isValid,
      error: result.error,
    };
  }

  /**
   * Asinhrona validacija email domena (preporučeno)
   * @param {string} email - The email to validate
   * @returns {Promise<ValidationResult>} - Result with isValid and error message
   */
  async validateEmailDomainAsync(email: string): Promise<ValidationResult> {
    const result = await emailDomainValidator.validateEmailDomainAsync(email);
    return {
      isValid: result.isValid,
      error: result.error,
    };
  }

  /**
   * Validates an email address (with domain checking for registration)
   * @param {string} email - The email to validate
   * @param {boolean} checkDomain - Whether to check domain (default: true)
   * @returns {ValidationResult} - Result with isValid and error message
   */
  validateEmail(email: string, checkDomain: boolean = true): ValidationResult {
    const sanitizedEmail = securityService.sanitizeInput(email);

    if (!sanitizedEmail) {
      return { isValid: false, error: 'Email adresa je obavezna' };
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sanitizedEmail)) {
      return { isValid: false, error: 'Unesite validnu email adresu' };
    }

    // Za registraciju proveravamo i domen
    if (checkDomain) {
      const domainValidation = this.validateEmailDomain(sanitizedEmail);
      if (!domainValidation.isValid) {
        return domainValidation;
      }
    }

    return { isValid: true, error: '' };
  }

  /**
   * Asinhrona validacija email adrese (preporučeno za registraciju)
   * @param {string} email - The email to validate
   * @param {boolean} checkDomain - Whether to check domain (default: true)
   * @returns {Promise<ValidationResult>} - Result with isValid and error message
   */
  async validateEmailAsync(
    email: string,
    checkDomain: boolean = true
  ): Promise<ValidationResult> {
    const sanitizedEmail = securityService.sanitizeInput(email);

    if (!sanitizedEmail) {
      return { isValid: false, error: 'Email adresa je obavezna' };
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sanitizedEmail)) {
      return { isValid: false, error: 'Unesite validnu email adresu' };
    }

    // Za registraciju proveravamo i domen
    if (checkDomain) {
      const domainValidation = await this.validateEmailDomainAsync(
        sanitizedEmail
      );
      if (!domainValidation.isValid) {
        return domainValidation;
      }
    }

    return { isValid: true, error: '' };
  }

  /**
   * Validates email format only (for login - without domain checking)
   * @param {string} email - The email to validate
   * @returns {ValidationResult} - Result with isValid and error message
   */
  validateEmailFormat(email: string): ValidationResult {
    return this.validateEmail(email, false);
  }

  /**
   * Quick check if email domain is allowed
   * @param {string} email - The email to check
   * @returns {boolean} - True if domain is allowed
   */
  isEmailDomainAllowed(email: string): boolean {
    return emailDomainValidator.isEmailDomainAllowedSync(email);
  }

  /**
   * Asinhrona provera email domena
   * @param {string} email - The email to check
   * @returns {Promise<boolean>} - True if domain is allowed
   */
  async isEmailDomainAllowedAsync(email: string): Promise<boolean> {
    return await emailDomainValidator.isEmailDomainAllowed(email);
  }

  /**
   * Get list of allowed email domains
   * @returns {string[]} - Array of allowed domains
   */
  getAllowedEmailDomains(): string[] {
    return emailDomainValidator.getAllowedDomains();
  }

  /**
   * Get blacklist statistics (for debug)
   * @returns {number} - Number of blacklisted domains
   */
  getBlacklistSize(): number {
    return emailDomainValidator.getBlacklistSize();
  }

  /**
   * Refresh email blacklist manually
   * @returns {Promise<void>}
   */
  async refreshEmailBlacklist(): Promise<void> {
    await emailDomainValidator.refreshBlacklist();
  }

  /**
   * Validates a password with stronger requirements
   * @param {string} password - The password to validate
   * @returns {ValidationResult} - Result with isValid and error message
   */
  validatePassword(password: string): ValidationResult {
    if (!password) {
      return { isValid: false, error: 'Lozinka je obavezna' };
    }

    const errors = [];

    // Check minimum length
    if (password.length < 8) {
      errors.push('najmanje 8 karaktera');
    }

    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
      errors.push('jedno veliko slovo');
    }

    // Check for lowercase letter
    if (!/[a-z]/.test(password)) {
      errors.push('jedno malo slovo');
    }

    // Check for number
    if (!/[0-9]/.test(password)) {
      errors.push('jedan broj');
    }

    if (errors.length > 0) {
      return {
        isValid: false,
        error: `Lozinka mora sadržati ${errors.join(', ')}`,
      };
    }

    return { isValid: true, error: '' };
  }

  /**
   * Validates that two passwords match
   * @param {string} password - The original password
   * @param {string} confirmPassword - The confirmation password
   * @returns {ValidationResult} - Result with isValid and error message
   */
  validatePasswordMatch(
    password: string,
    confirmPassword: string
  ): ValidationResult {
    if (!confirmPassword) {
      return { isValid: false, error: 'Potvrdna lozinka je obavezna' };
    }

    if (password !== confirmPassword) {
      return { isValid: false, error: 'Lozinke se ne podudaraju' };
    }

    return { isValid: true, error: '' };
  }

  /**
   * Validates a username
   * @param {string} username - The username to validate
   * @returns {ValidationResult} - Result with isValid and error message
   */
  validateUsername(username: string): ValidationResult {
    const sanitizedUsername = securityService.sanitizeInput(username);

    if (!sanitizedUsername) {
      return { isValid: false, error: 'Korisničko ime je obavezno' };
    }

    if (sanitizedUsername.length < 3) {
      return {
        isValid: false,
        error: 'Korisničko ime mora imati najmanje 3 karaktera',
      };
    }

    if (sanitizedUsername.length > 30) {
      return {
        isValid: false,
        error: 'Korisničko ime može imati najviše 30 karaktera',
      };
    }

    // NOVA VALIDACIJA - blokiranje srpskih slova
    if (/[šđčćžŠĐČĆŽ]/.test(sanitizedUsername)) {
      return {
        isValid: false,
        error: 'Korisničko ime ne može sadržati slova (š,đ,č,ć,ž)',
      };
    }

    // Provera da li sadrži samo dozvoljena karaktera
    if (!/^[a-zA-Z0-9_]+$/.test(sanitizedUsername)) {
      return {
        isValid: false,
        error:
          'Korisničko ime može sadržati samo slova, brojeve i i donju crtu (_).',
      };
    }

    // Provera rezervisanih imena
    const reservedUsernames = ['admin', 'superuser', 'root'];
    if (reservedUsernames.includes(sanitizedUsername.toLowerCase())) {
      return {
        isValid: false,
        error: 'Ovo korisničko ime nije dozvoljeno',
      };
    }

    return { isValid: true, error: '' };
  }

  /**
   * Validates that a required field is not empty
   * @param {string} value - The field value
   * @param {string} fieldName - The field name
   * @returns {ValidationResult} - Result with isValid and error message
   */
  validateRequired(value: string, fieldName: string): ValidationResult {
    const sanitizedValue = securityService.sanitizeInput(value);

    if (!sanitizedValue) {
      return { isValid: false, error: `${fieldName} je obavezno` };
    }

    return { isValid: true, error: '' };
  }
}

// Create singleton instance
const formValidator = new FormValidator();

export default formValidator;
